/**
 * Cancel a field event: reason plus optional detail.
 */
import * as React from 'react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Select } from '@/components/forms/Select';
import { Textarea } from '@/components/forms/Textarea';
import { Button } from '@/components/forms/Button';
import { EVENT_CANCEL_REASON_OPTIONS } from './event-configs';
import { useFormValidation } from '@/hooks/useFormValidation';
import { cancelEventValidationSchema } from '@/utils/validation-schemas-utils';

/** Props for {@link CancelEventModal}. */
interface CancelEventModalProps {
  eventId: string;
  onClose: () => void;
  onConfirm: (reason: string, detail?: string) => void;
}

/** Cancel a field event: reason plus optional detail. */
export const CancelEventModal: React.FC<CancelEventModalProps> = ({
  eventId: _eventId,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = React.useState('');
  const [detail, setDetail] = React.useState('');
  const validation = useFormValidation(cancelEventValidationSchema);

  function handleReasonChange(value: string): void {
    setReason(value);
    validation.validateField('reason', value, { reason: value, detail });
    validation.markAsTouched('reason');
  }

  function handleDetailChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    setDetail(event.target.value);
  }

  function handleConfirm(): void {
    if (!validation.validateForm({ reason, detail })) return;
    onConfirm(reason, detail || undefined);
  }

  return (
    <BaseModal
      open={true}
      onClose={onClose}
      header={{
        preset: 'danger',
        title: "Annuler l'événement",
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      footer={
        <div className="flex gap-2 justify-end">
          <Button preset="modal-cancel" onClick={onClose}>
            Fermer
          </Button>
          <Button variant="danger" onClick={handleConfirm} disabled={!validation.isFormValid()}>
            {"Annuler l'événement"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Cette action ne peut pas être annulée. L'événement sera marqué comme annulé.
        </p>

        <Select
          label="Raison de l'annulation"
          placeholder="Sélectionner une raison"
          options={[...EVENT_CANCEL_REASON_OPTIONS]}
          value={reason}
          onChange={handleReasonChange}
          required
          state={validation.getFieldState('reason')}
          error={validation.getFieldError('reason')}
        />

        <Textarea
          label="Détails (optionnel)"
          placeholder="Précisez les détails de l'annulation..."
          value={detail}
          onChange={handleDetailChange}
          rows={4}
        />
      </div>
    </BaseModal>
  );
};
